import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Skeleton, Stack, Typography, Chip, Divider } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { MyNextAppointment, getMyAppointments } from "../../services/my-appointments.service";
import { toast } from "sonner";

export function MyAppointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<MyNextAppointment[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const data = await getMyAppointments();
        if (!ignore) setAppointments(data);
      } catch (e) {
        if (!ignore) toast.error("Não foi possível carregar seus agendamentos.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, []);

  const hasAppointments = !!appointments && appointments.length > 0;

  return (
    <Box
      width="100%"
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 900, mx: "auto", mb: 2 }}>
        <Typography variant="h5" fontWeight={600} color="#133859">
          Meus agendamentos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Veja todas as suas consultas agendadas de forma simplificada.
        </Typography>
      </Box>

      {loading && (
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent>
                <Stack spacing={1}>
                  <Skeleton variant="text" width={180} />
                  <Skeleton variant="text" width={120} />
                  <Skeleton variant="text" width={220} />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && !hasAppointments && (
        <Box sx={{ width: "100%", maxWidth: 900, mx: "auto", mt: 4 }}>
          <Card>
            <CardContent>
              <Typography align="center" color="text.secondary">
                Você ainda não possui agendamentos.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {!loading && hasAppointments && (
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
          {appointments!.map((appt) => (
            <Card key={appt.id}>
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600} color="#133859">
                      {dayjs(appt.startTime).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                    <Chip
                      size="small"
                      color={appt.status === "CONFIRMADO" ? "success" : appt.status === "CANCELADO" ? "error" : "warning"}
                      label={appt.status}
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    Médico: <strong>{appt.doctorName || "-"}</strong>
                  </Typography>

                  {appt.pin && (
                    <Typography variant="body2" color="text.secondary">
                      PIN: <strong>{appt.pin}</strong>
                    </Typography>
                  )}

                  {appt.local && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        {appt.local.localName || appt.local.id}
                        {(appt.local.address || appt.local.city || appt.local.state) && (
                          <> — {`${appt.local.address || ""}${appt.local.city || appt.local.state ? " " : ""}${appt.local.city || ""}${appt.local.state ? ` / ${appt.local.state}` : ""}`}</>
                        )}
                      </Typography>
                    </>
                  )}

                  {appt.medicalRecordId && (
                    <Stack direction="row" justifyContent="flex-end" sx={{ pt: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ArticleOutlinedIcon />}
                        onClick={() => navigate(`/prontuario/${appt.medicalRecordId}`)}
                        sx={{ borderColor: "#133859", color: "#133859" }}
                      >
                        Ver prontuário
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
